/**
 * React Query hooks for location scheduling (Web)
 * Provides data fetching, caching, mutations, and optimistic updates
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/schedulingApi';
import { SchedulingMode } from '../types/schedule';
import type {
  ScheduleDto,
  ScheduleItem,
  SchedulingRule,
  CreateSchedulingRuleRequest,
  UpdateSchedulingRuleRequest,
} from '../types/schedule';

/**
 * Query key factory for schedule-related queries
 */
export const scheduleKeys = {
  all: ['schedule'] as const,
  byLocation: (locationId: string) => [...scheduleKeys.all, 'location', locationId] as const,
  items: (locationId: string) => [...scheduleKeys.byLocation(locationId), 'items'] as const,
  item: (locationId: string, itemId: string) =>
    [...scheduleKeys.items(locationId), itemId] as const,
  rules: (locationId: string, itemId: string) =>
    [...scheduleKeys.item(locationId, itemId), 'rules'] as const,
  rule: (locationId: string, itemId: string, ruleId: string) =>
    [...scheduleKeys.rules(locationId, itemId), ruleId] as const,
};

/**
 * Fetch a location's complete schedule
 */
export function useLocationSchedule(locationId: string) {
  return useQuery({
    queryKey: scheduleKeys.byLocation(locationId),
    queryFn: () => api.getSchedule(locationId),
    enabled: !!locationId,
  });
}

/**
 * Add a playlist to the schedule
 */
export function useAddPlaylist(locationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (playlistId: string) => api.addPlaylist(locationId, playlistId),
    onMutate: async (playlistId: string) => {
      await queryClient.cancelQueries({ queryKey: scheduleKeys.byLocation(locationId) });

      const previous = queryClient.getQueryData<ScheduleDto>(
        scheduleKeys.byLocation(locationId)
      );

      if (previous) {
        const optimisticItem: ScheduleItem = {
          id: 'temp-' + Date.now(),
          position: (previous.items?.length || 0) + 1,
          type: 'playlist',
          playlistId,
          playlistName: 'Loading...',
          playlistType: 'Music',
          rules: [],
          createdAt: new Date().toISOString(),
        };
        queryClient.setQueryData<ScheduleDto>(scheduleKeys.byLocation(locationId), {
          ...previous,
          items: [...(previous.items || []), optimisticItem],
        });
      }

      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.byLocation(locationId) });
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(scheduleKeys.byLocation(locationId), context.previous);
      }
    },
  });
}

/**
 * Add a clip to the schedule
 */
export function useAddClip(locationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contentId: string) => api.addClip(locationId, contentId),
    onMutate: async (contentId: string) => {
      await queryClient.cancelQueries({ queryKey: scheduleKeys.byLocation(locationId) });

      const previous = queryClient.getQueryData<ScheduleDto>(
        scheduleKeys.byLocation(locationId)
      );

      if (previous) {
        const optimisticItem: ScheduleItem = {
          id: 'temp-' + Date.now(),
          position: (previous.items?.length || 0) + 1,
          type: 'clip',
          contentId,
          contentName: 'Loading...',
          rules: [],
          createdAt: new Date().toISOString(),
        };
        queryClient.setQueryData<ScheduleDto>(scheduleKeys.byLocation(locationId), {
          ...previous,
          items: [...(previous.items || []), optimisticItem],
        });
      }

      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.byLocation(locationId) });
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(scheduleKeys.byLocation(locationId), context.previous);
      }
    },
  });
}

/**
 * Remove an item from the schedule
 */
export function useRemoveItem(locationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) => api.removeItem(locationId, itemId),
    onMutate: async (itemId: string) => {
      await queryClient.cancelQueries({ queryKey: scheduleKeys.byLocation(locationId) });

      const previous = queryClient.getQueryData<ScheduleDto>(
        scheduleKeys.byLocation(locationId)
      );

      if (previous) {
        queryClient.setQueryData<ScheduleDto>(scheduleKeys.byLocation(locationId), {
          ...previous,
          items: previous.items.filter((item) => item.id !== itemId),
        });
      }

      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.byLocation(locationId) });
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(scheduleKeys.byLocation(locationId), context.previous);
      }
    },
  });
}

/**
 * Reorder items in the schedule
 */
export function useReorderItems(locationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemIds: string[]) => api.reorderItems(locationId, itemIds),
    onMutate: async (itemIds: string[]) => {
      await queryClient.cancelQueries({ queryKey: scheduleKeys.byLocation(locationId) });

      const previous = queryClient.getQueryData<ScheduleDto>(
        scheduleKeys.byLocation(locationId)
      );

      if (previous) {
        const itemMap = new Map(previous.items.map((item) => [item.id, item]));
        const reorderedItems = itemIds
          .map((id) => itemMap.get(id))
          .filter((item) => item !== undefined) as ScheduleItem[];

        queryClient.setQueryData<ScheduleDto>(scheduleKeys.byLocation(locationId), {
          ...previous,
          items: reorderedItems.map((item, idx) => ({
            ...item,
            position: idx + 1,
          })),
        });
      }

      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.byLocation(locationId) });
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(scheduleKeys.byLocation(locationId), context.previous);
      }
    },
  });
}

/**
 * Create a scheduling rule for an item
 */
export function useCreateSchedulingRule(locationId: string, itemId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateSchedulingRuleRequest) =>
      api.createRule(locationId, itemId, request),
    onMutate: async (request: CreateSchedulingRuleRequest) => {
      await queryClient.cancelQueries({ queryKey: scheduleKeys.byLocation(locationId) });

      const previous = queryClient.getQueryData<ScheduleDto>(
        scheduleKeys.byLocation(locationId)
      );

      if (previous) {
        const ruleBase = {
          id: 'temp-' + Date.now(),
          activeFrom: request.activeFrom ?? null,
          activeUntil: request.activeUntil ?? null,
          badge: 'Loading...',
          createdAt: new Date().toISOString(),
        };
        const optimisticRule: SchedulingRule =
          request.schedulingMode === SchedulingMode.FREQUENCY
            ? { ...ruleBase, schedulingMode: SchedulingMode.FREQUENCY, frequency: request.frequency ?? 1 }
            : {
                ...ruleBase,
                schedulingMode: SchedulingMode.SPECIFIC_TIMES,
                specificTimes: request.specificTimes ?? [],
              };

        const updatedItems = previous.items.map((item) => {
          if (item.id === itemId) {
            return {
              ...item,
              rules: [...item.rules, optimisticRule],
            };
          }
          return item;
        });

        queryClient.setQueryData<ScheduleDto>(scheduleKeys.byLocation(locationId), {
          ...previous,
          items: updatedItems,
        });
      }

      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.byLocation(locationId) });
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(scheduleKeys.byLocation(locationId), context.previous);
      }
    },
  });
}

/**
 * Update a scheduling rule
 */
export function useUpdateSchedulingRule(
  locationId: string,
  itemId: string,
  ruleId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateSchedulingRuleRequest) =>
      api.updateRule(locationId, itemId, ruleId, request),
    onMutate: async (request: UpdateSchedulingRuleRequest) => {
      await queryClient.cancelQueries({ queryKey: scheduleKeys.byLocation(locationId) });

      const previous = queryClient.getQueryData<ScheduleDto>(
        scheduleKeys.byLocation(locationId)
      );

      if (previous) {
        const updatedItems = previous.items.map((item) => {
          if (item.id === itemId) {
            return {
              ...item,
              rules: item.rules.map((rule) => {
                if (rule.id === ruleId) {
                  return { ...rule, ...request } as SchedulingRule;
                }
                return rule;
              }),
            };
          }
          return item;
        });

        queryClient.setQueryData<ScheduleDto>(scheduleKeys.byLocation(locationId), {
          ...previous,
          items: updatedItems,
        });
      }

      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.byLocation(locationId) });
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(scheduleKeys.byLocation(locationId), context.previous);
      }
    },
  });
}

/**
 * Delete a scheduling rule
 */
export function useDeleteSchedulingRule(
  locationId: string,
  itemId: string,
  ruleId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.deleteRule(locationId, itemId, ruleId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: scheduleKeys.byLocation(locationId) });

      const previous = queryClient.getQueryData<ScheduleDto>(
        scheduleKeys.byLocation(locationId)
      );

      if (previous) {
        const updatedItems = previous.items.map((item) => {
          if (item.id === itemId) {
            return {
              ...item,
              rules: item.rules.filter((rule) => rule.id !== ruleId),
            };
          }
          return item;
        });

        queryClient.setQueryData<ScheduleDto>(scheduleKeys.byLocation(locationId), {
          ...previous,
          items: updatedItems,
        });
      }

      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.byLocation(locationId) });
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(scheduleKeys.byLocation(locationId), context.previous);
      }
    },
  });
}
